/**
 * Locale detection and state management.
 * @module locale
 */

import { loadTranslations } from "./translations/index.js";

export type Locale = "en" | "fr";

function detectLocale(): Locale {
	try {
		const stored = localStorage.getItem("qp_locale");
		if (stored === "fr" || stored === "en") return stored;
	} catch {
		// localStorage may not be available (SSR, tests)
	}
	try {
		if (navigator.language.startsWith("fr")) return "fr";
	} catch {
		// navigator may not be available
	}
	return "en";
}

let currentLocale: Locale = detectLocale();

/**
 * Resolves when the translation table for the locale detected at startup has
 * loaded (immediately for English). Await this — or use it as an async atom
 * default — before the first localized render for non-English users.
 */
export const localeReady: Promise<void> = loadTranslations(currentLocale);

export function getLocale(): Locale {
	return currentLocale;
}

/**
 * Switches the locale immediately; returns a promise that resolves once the
 * locale's translation table is loaded. Await it before triggering re-renders
 * (e.g. before setting a locale atom) so subscribers see translated strings.
 */
export function setLocale(locale: Locale): Promise<void> {
	currentLocale = locale;
	try {
		localStorage.setItem("qp_locale", locale);
	} catch {
		// localStorage may not be available
	}
	return loadTranslations(locale);
}
